import { ChangeDetectionStrategy, Component, inject, OnInit, QueryList, ViewChildren } from '@angular/core';
import { SafeHtml } from '@angular/platform-browser';
import { UserPersonalInformationDto } from '@api-proxies';
import { AuthService } from '@app/services/auth.service';
import { AppEditorFactory } from '@factories/app-editor-factory/app-editor-factory';
import { EditorType } from '@factories/app-editor-factory/enum/editor-type';
import { UserService } from '@http/clients/user.service';
import { AwaiterService } from '@services/awaiter.service';
import { CDK_IMPORTS } from '@utils/standalone-imports';
import {
    EditorItemComponent,
    IconSanitizerService,
    IEditorItemConfig,
    ModalComponentBase,
    SnackbarNotificationService,
    ValidatorType
} from 'ngx-lib';

@Component({
    selector: 'app-modal-user-account-settings',
    templateUrl: './modal-user-account-settings.component.html',
    styleUrls: ['./modal-user-account-settings.component.scss'],
    changeDetection: ChangeDetectionStrategy.Eager,
    imports: [...CDK_IMPORTS, EditorItemComponent]
})
export class ModalUserAccountSettingsComponent extends ModalComponentBase<undefined, undefined> implements OnInit {
    private readonly userService = inject(UserService);
    private readonly authService = inject(AuthService);
    private readonly snackbarNotificationService = inject(SnackbarNotificationService);
    private readonly iconSanitizer = inject(IconSanitizerService);
    private readonly awaiter = inject(AwaiterService);

    @ViewChildren('editorItem')
    editors?: QueryList<EditorItemComponent>;

    factory: AppEditorFactory = new AppEditorFactory();
    configItems: IEditorItemConfig[] = [];
    user!: UserPersonalInformationDto;
    private originalEmail = '';

    closeIcon: SafeHtml = this.iconSanitizer.getIcon('closeModal');

    constructor() {
        super();
    }

    async ngOnInit(): Promise<void> {
        await this.awaiter.awaitAction('Getting user information...', async () => {
            this.user = await this.userService.getUserInformation();
            this.originalEmail = this.user.email ?? '';

            this.configItems = [
                {
                    label: 'Email',
                    componentId: EditorType.textInput,
                    fieldName: 'email',
                    bindContext: this.user,
                    validations: [
                        { type: ValidatorType.required },
                        { type: ValidatorType.email },
                        { type: ValidatorType.string, args: { maxLength: 256 } }
                    ],
                    additionalInfo: {
                        shouldTrimSpaces: true,
                        showOnlyFirstError: true
                    }
                },
                {
                    label: 'First name',
                    componentId: EditorType.textInput,
                    fieldName: 'firstName',
                    bindContext: this.user,
                    validations: [
                        { type: ValidatorType.required },
                        { type: ValidatorType.string, args: { maxLength: 50 } }
                    ],
                    additionalInfo: {
                        shouldTrimSpaces: true
                    }
                },
                {
                    label: 'Last name',
                    componentId: EditorType.textInput,
                    fieldName: 'lastName',
                    bindContext: this.user,
                    validations: [
                        { type: ValidatorType.required },
                        { type: ValidatorType.string, args: { maxLength: 60 } }
                    ],
                    additionalInfo: {
                        shouldTrimSpaces: true
                    }
                },
                {
                    label: 'Title / Position',
                    componentId: EditorType.textInput,
                    fieldName: 'title',
                    bindContext: this.user,
                    validations: [{ type: ValidatorType.string, args: { maxLength: 100 } }]
                }
            ];
        });
    }

    validate(): boolean {
        return this.editors?.map((editorItem) => editorItem.validate()).every((editorItem) => editorItem) ?? false;
    }

    async saveSettings(): Promise<void> {
        this.sanitizeData();

        if (!this.validate()) return;

        await this.awaiter.awaitAction('Saving user information...', async () => {
            await this.userService.changeUserInformation(this.user);
            const emailChanged = this.user.email !== this.originalEmail;
            if (emailChanged) {
                this.snackbarNotificationService.info(
                    'Confirmation email sent. Your email address will be updated once you confirm the change using the link in the email.',
                    8000
                );
                await this.authService.logout();
            } else {
                this.authService.updateUserInfo({
                    email: this.user.email!,
                    firstName: this.user.firstName!,
                    lastName: this.user.lastName!
                });

                this.snackbarNotificationService.success('User information was successfully updated');
                this.close(undefined);
            }
        });
    }

    private sanitizeData() {
        Object.keys(this.user).forEach((key) => {
            const value = (this.user as unknown as Record<string, string>)[key];
            if (typeof value === 'string') {
                (this.user as any)[key] = value.trim();
            }
        });
    }
}
